import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery } from '@mui/material';
import { ImageSquare } from '@phosphor-icons/react';

import {
  PlanCardHeaderSyled,
  CarrierLogoBoxStyled,
} from 'enrollment/features/Plans/PlanCard/PlanCardStyled';
import {
  CoverageLineDto,
  IncrementDto,
  PlanRiderDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { SpecificIcon } from 'enrollment/utils/common/Utils';
import { IncrementType, VolumeCardProps } from '../../Volume/VolumeCard';
import { EsignatureModal } from '../../EsignatureModal';
import { volumeElectionParams } from '../../Volume/utils';
import { StackedVolumeSelect } from './StackedVolumeSelect';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import {
  StackedCoverageContainer,
  StackedTotalCostContainer,
} from './StackedVolumeStyled';
import { StackedVolumeSlider } from './StackedVolumeSlider';
import { ContributionTable } from 'enrollment/components/ContributionsCard/components';
import { selectPerPayPeriodText } from 'enrollment/services';

export interface StackedVolumeCardProps
  extends Omit<VolumeCardProps, 'increments'> {
  increments: { [key: string]: IncrementDto[] };
  coverageLines: CoverageLineDto[];
}

export const StackedVolumeCard = ({
  plan,
  orgBenefitId,
  enrollmentContextType,
  selectedDependentIds,
  color,
  benefitLookup,
  updateElection,
  showEsignatureModal,
  orgPlanYearId,
  increments,
  coverageLines,
  hideEmptyCost,
}: StackedVolumeCardProps) => {
  const { t } = useTranslation('pages.volume');
  const perPayText = useSelector(selectPerPayPeriodText);
  const isMobile = useMediaQuery('(max-width: 744px)');

  const defaultIncrement = {
    increment: '0',
    volume: 'Select Amount',
    subscriber_premium: '0.0',
    requires_eoi: false,
  } as IncrementType;

  const [electedRiders, setElectedRiders] = useState<number[]>([]);
  const incrementObj = useMemo(() => increments || [], [increments]);

  const requestedIncrements = coverageLines.map((covLine) => {
    const increments =
      incrementObj[`coverage_line_${covLine.id}`] ||
      incrementObj[`coverage_line_0`];
    if (!increments) {
      return {
        increment: covLine.req_increments,
        volume: covLine.req_volume,
        subscriber_premium: covLine.req_subscriber_premium,
        requires_eoi: false,
      } as IncrementType;
    }
    return (
      increments.find(
        (increment) =>
          parseFloat(increment.increment) === parseFloat(covLine.req_increments)
      ) || defaultIncrement
    );
  });

  const [selectedIncrements, setSelectedIncrements] = useState<IncrementType[]>(
    requestedIncrements.map((reqInc) => reqInc || defaultIncrement) ||
    defaultIncrement
  );
  const [totalElected, setTotalElected] = useState(0);
  const [totalCostVolume, setTotalCostVolume] = useState(() => {
    return selectedIncrements.map((inc) => parseFloat(inc.subscriber_premium));
  });
  const [totalCostRiders, setTotalCostRiders] = useState(0);
  const [totalCostValue, setTotalCostValue] = useState(
    totalCostVolume.reduce((acc, cost) => acc + cost, 0)
  );

  const lastNonEoiIncrements = Object.values(incrementObj).map((increments) => {
    const incArr = increments.filter((inc) => !inc.requires_eoi); // Filter out increments that require EOI
    if (incArr.length !== increments.length) {
      return incArr.pop(); // Get the last element
    } else {
      return undefined;
    }
  });

  const riderProps = {
    orgPlanRiderBenefits: plan.org_plan_rider_benefits as PlanRiderDto[],
    electedRiders,
    setElectedRiders,
    orgPlanYearId,
    setTotalCostRiders: setTotalCostRiders,
  };

  const getMaxElection = () => {
    let max = 0;
    coverageLines.forEach((covLine) => {
      const increments =
        incrementObj[`coverage_line_${covLine.id}`] ||
        incrementObj[`coverage_line_0`];
      if (!increments) {
        max += Number(covLine.req_volume);
      } else {
        max += Number(increments[increments.length - 1].volume);
      }
    });
    return max;
  };

  const handleElection = (data?: any) => {
    const selectedVolumes = selectedIncrements.map((inc) => inc.volume);
    const selectedIncrementValues: { [key: string]: string } = {};
    if (selectedIncrements.length >= 1) {
      coverageLines.forEach((covLine, index) => {
        selectedIncrementValues[String(covLine.id)] =
          selectedIncrements[index].increment;
      });
    }

    let selectedIncsVal = undefined;
    if (
      selectedIncrementValues &&
      Object.values(selectedIncrementValues).length === 0
    ) {
      selectedIncsVal = selectedIncrementValues;
    }
    const submitCovLineId =
      coverageLines.find((covLine) => covLine.in_current_plan_year)?.id || 0;

    if (data) {
      updateElection(
        volumeElectionParams({
          plan,
          orgBenefitId,
          enrollmentContextType,
          selectedDependentIds,
          selectedIncrementValues: selectedIncsVal,
          selectedVolumes,
          esignPassword: data?.esign_password,
          esignChecked: data?.esign_checked,
          electedRiders,
          coverageLineId: submitCovLineId,
        })
      );
    } else {
      updateElection(
        volumeElectionParams({
          plan,
          orgBenefitId,
          enrollmentContextType,
          selectedDependentIds,
          selectedIncrementValues,
          selectedVolumes,
          electedRiders,
          coverageLineId: submitCovLineId,
        })
      );
    }
  };

  const emptyFunction = () => {
    return;
  };

  const electVolume = () => {
    if (!selectedIncrements?.some((inc) => inc.volume)) {
      return;
    }

    handleElection();
  };

  useEffect(() => {
    let total = 0;
    selectedIncrements.forEach((inc) => {
      const value = parseFloat(inc.subscriber_premium);
      if (!isNaN(value)) {
        total += value;
      }
    });
    total += totalCostRiders;
    setTotalCostValue(total);
  }, [totalCostRiders, selectedIncrements]);

  return (
    <div className="card p-0" data-testid="volume-card-container">
      <PlanCardHeaderSyled>
        <div className={`icon-container ${color}`}>
          <SpecificIcon benefitType={benefitLookup ? benefitLookup : ''} />
        </div>
        <h2 className="d-none d-md-inline ml-3_5 plan-card">
          {plan.plan_name}
        </h2>
        <CarrierLogoBoxStyled>
          {plan?.carrier_logo_url ?
            <img
              src={plan?.carrier_logo_url}
              alt=""
              data-testid="carrier-logo"
            />
            :
            <div className="d-flex align-items-center">
              <ImageSquare size={32} weight="duotone" color="#56638a" />
              <Typography color="#56638a">{plan.carrier}</Typography>
            </div>
          }
        </CarrierLogoBoxStyled>

      </PlanCardHeaderSyled>
      <StackedVolumeSlider
        totalElected={totalElected}
        min={0}
        max={getMaxElection()}
        lastNonEoiIncrements={lastNonEoiIncrements}
      />
      {selectedIncrements.some((inc) => inc.requires_eoi) && (
        <div className="mx-5 mt-3 border-bottom pb-3">
          <PSBannerWrapper icon level="info" message={t('requiresEOI')} />
        </div>
      )}
      {Object.values(incrementObj).length > 0 && (
        <div className="mx-5 mt-3">
          <PSBannerWrapper
            message={`${t('pages.stackedVolume:howWorks')} ${t('pages.stackedVolume:howWorksBody')}`}
            level="info"
            icon
          />
        </div>
      )}
      <StackedCoverageContainer className="px-4 px-md-5">
        {Object.values(coverageLines).map((coverageLine, index) => {
          return (
            <StackedVolumeSelect
              increments={
                incrementObj[`coverage_line_${coverageLine.id}`] ||
                incrementObj[`coverage_line_0`] ||
                []
              }
              coverageIndex={index}
              selectedIncrements={selectedIncrements}
              setSelectedIncrements={setSelectedIncrements}
              setTotalElected={setTotalElected}
              setCost={setTotalCostVolume}
              planName={plan.plan_name}
              coverageLine={coverageLine}
              hideEmptyCost={hideEmptyCost}
              riderCoverages={coverageLine.rider_coverage_lines}
              {...riderProps}
            />
          );
        })}
      </StackedCoverageContainer>
      <StackedTotalCostContainer>
        {(!hideEmptyCost || totalCostValue > 0) && (
          <ContributionTable
            title={`${t('totalCost')} ${perPayText}`}
            value={totalCostValue.toFixed(2)}
          ></ContributionTable>
        )}
      </StackedTotalCostContainer>
      <div
        className={`border-top p-4 d-flex ${isMobile ? 'justify-content-center' : ''}`}
      >
        {showEsignatureModal ? (
          <EsignatureModal
            setEsignElectionData={handleElection}
            carrierLogoUrl={plan?.carrier_logo_url}
            carrierName={plan?.carrier}
            data={plan?.esign_data}
            planId={plan?.plan_id}
            setModalRequired={emptyFunction}
          />
        ) : (
          <ps-button
            id="elect-volume"
            other-data={{ 'data-testid': 'elect-volume-btn' }}
            level="primary"
            label={t('looksGood')}
            {...{ onClick: () => electVolume() }}
          ></ps-button>
        )}
      </div>
    </div>
  );
};
